import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  Line, AreaChart, Area, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import {
  Sun, Zap, Activity,
  Thermometer, CloudRain, Battery, Clock, MapPin, WifiOff, ChevronRight,
  CloudSun, Wind, Droplets, RefreshCw, Gauge
} from 'lucide-react';
import { format } from 'date-fns';
import '../components/AdvancedDashboard.css';
import './pages.css';
import { useAuth } from '../auth';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { LoadingState, EmptyState, ErrorState } from '../components/ui/States';
import { fetchPanelFieldHistory, fetchWeatherFieldHistory, mergeFieldSeries, sumMergedSeries, fetchPlantForecast, fetchActiveAlerts, fetchPlantEnergyToday } from '../historyApi';
import { wmoLabel, wmoIcon } from '../wmoWeatherCodes';
import { formatRelative } from '../relativeTime';

const PANEL_FIELDS = ['power_w', 'voltage_v', 'current_a', 'temp_panel_c'];
const WEATHER_FIELDS = ['light_lvl'];
const TABS = [
  { id: 'overview', label: 'Aperçu' },
  { id: 'panels', label: 'Panneaux' },
  { id: 'weather', label: 'Météo' },
  { id: 'history', label: 'Historique' },
  { id: 'forecast', label: 'Prévisions' }
];

const SEVERITY_LABELS = { INFO: 'Information', WARNING: 'Avertissement', CRITICAL: 'Critique' };

// Every forecast-triggered alert's message already ends with this exact
// suffix (see the forecast rule messages built in server.js's
// evaluateForecastRules) - trimmed only for the compact on-page risk card,
// since Open-Meteo attribution is already shown once at the bottom of this
// tab (section F). The full untrimmed message (with the suffix) is still
// what Telegram/email/the Alerts page show - this is a display-only tweak,
// not a change to the stored alert.
function trimForecastMessage(message) {
  return typeof message === 'string' ? message.replace(/ - source: Open-Meteo$/, '') : message;
}

// Open-Meteo's hourly rows are naive local time ("2026-08-24T19:00") with no
// day-boundary alignment to "now" - the array always starts at the first
// requested day's 00:00, so hourly[0] is midnight, not "the current hour".
// Using Intl's own timeZone-aware formatting (rather than manual UTC-offset
// arithmetic) finds the plant's real current local hour correctly and
// DST-safely, using only the IANA timezone name the forecast already
// carries - no new data dependency.
function findNearTermIndex(hourly, timezone) {
  if (!Array.isArray(hourly) || hourly.length === 0) return -1;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || undefined,
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const nowPrefix = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}`;
    const exact = hourly.findIndex((h) => typeof h.time === 'string' && h.time.startsWith(nowPrefix));
    if (exact !== -1) return exact;
    // No exact hour match (e.g. request landed between two cached fetches) -
    // fall back to the first row at or after "now" (safe: these strings
    // sort correctly as plain text since they share the same fixed format).
    const future = hourly.findIndex((h) => typeof h.time === 'string' && h.time >= nowPrefix);
    return future !== -1 ? future : 0;
  } catch {
    return 0;
  }
}

// "HH:mm" from Open-Meteo's local hourly timestamp ("YYYY-MM-DDTHH:mm") -
// slicing rather than re-parsing through Date/toLocaleString, which would
// reinterpret the string through the browser's own timezone instead of the
// plant's (Open-Meteo already resolved and returned it in local time via
// timezone=auto).
function localHour(isoLike) {
  return typeof isoLike === 'string' && isoLike.length >= 16 ? isoLike.slice(11, 16) : '--:--';
}
function localDateLabel(dateStr) {
  if (typeof dateStr !== 'string') return dateStr;
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}`;
}

function combineByTime(seriesA, seriesB) {
  const byTime = new Map();
  seriesA.forEach(row => byTime.set(row._time, { ...row }));
  seriesB.forEach(row => {
    const existing = byTime.get(row._time) || { _time: row._time };
    byTime.set(row._time, { ...existing, ...row });
  });
  return Array.from(byTime.values()).sort((a, b) => new Date(a._time) - new Date(b._time));
}

export default function PlantDetailPage() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const { realTimeData, weatherData, plants, socket } = useOutletContext();
  const { token, user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('1h');
  const [selectedMetric, setSelectedMetric] = useState('power');
  const [historyData, setHistoryData] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('loading');
  const [forecast, setForecast] = useState(null);
  const [forecastStatus, setForecastStatus] = useState('idle'); // idle|loading|ok|error
  const [plantAlerts, setPlantAlerts] = useState(null); // active alerts (all plants caller can see), null = not loaded
  const [plantAlertsStatus, setPlantAlertsStatus] = useState('idle'); // idle|loading|ok|error
  const [energyToday, setEnergyToday] = useState(null);
  const [energyTodayStatus, setEnergyTodayStatus] = useState('idle'); // idle|loading|ok|error
  const [now, setNow] = useState(Date.now());

  const plantMeta = useMemo(
    () => (Array.isArray(plants) ? plants.find(p => p.plant_id === plantId) : null),
    [plants, plantId]
  );

  const realTimeArray = Array.from(realTimeData?.values?.() || []);
  const plantPanels = realTimeArray.filter(d => d.plant_id === plantId);
  const weatherArray = Array.from(weatherData?.values?.() || []);
  const plantWeather = weatherArray.filter(w => w.plant_id === plantId);

  const panelIds = useMemo(() => [...new Set(plantPanels.map(p => p.panel_id))], [plantPanels]);
  const stationIds = useMemo(() => [...new Set(plantWeather.map(w => w.station_id).filter(Boolean))], [plantWeather]);

  // Root cause of the permanently-stuck "Chargement de l'historique...":
  // panelIds/stationIds are new array references on every render (plantPanels/
  // plantWeather are recomputed straight from the live realTimeData/weatherData
  // Socket.IO maps in the render body, not memoized against anything stable),
  // and the outlet context object itself (App.js) is rebuilt on every stats
  // poll (~10s) and every live sensor_data/weather_data event (~10s each) -
  // so this component re-renders roughly every few seconds regardless of
  // whether the actual set of panels/stations changed. With the array
  // objects themselves as effect dependencies, that re-render churn re-ran
  // this effect just as often, cancelling the previous in-flight fetch
  // (via the `cancelled` flag) before it could ever call setHistoryStatus.
  // Under real network latency (this was reported from a phone on the LAN,
  // not localhost) the fetch can lose that race indefinitely, leaving the
  // state stuck at 'loading' forever even though every individual request
  // succeeds. Depending on the *contents* (joined into a stable string)
  // instead of the array reference stops the effect from re-running unless
  // the actual set of panel/station ids changes.
  const panelIdsKey = panelIds.join(',');
  const stationIdsKey = stationIds.join(',');

  useEffect(() => {
    if (panelIds.length === 0 && stationIds.length === 0) {
      setHistoryData([]);
      setHistoryStatus('empty');
      return;
    }
    let cancelled = false;
    setHistoryStatus('loading');

    const panelSeriesPromises = panelIds.map(panelId =>
      Promise.all(PANEL_FIELDS.map(field => fetchPanelFieldHistory(token, plantId, panelId, field, timeRange)))
        .then(results => {
          const fieldResults = {};
          PANEL_FIELDS.forEach((field, i) => { fieldResults[field] = results[i]; });
          return mergeFieldSeries(fieldResults);
        })
    );
    const weatherSeriesPromises = stationIds.map(stationId =>
      Promise.all(WEATHER_FIELDS.map(field => fetchWeatherFieldHistory(token, plantId, stationId, field, timeRange)))
        .then(results => {
          const fieldResults = {};
          WEATHER_FIELDS.forEach((field, i) => { fieldResults[field] = results[i]; });
          return mergeFieldSeries(fieldResults);
        })
    );

    Promise.all([Promise.all(panelSeriesPromises), Promise.all(weatherSeriesPromises)])
      .then(([panelSeriesList, weatherSeriesList]) => {
        if (cancelled) return;
        const panelSummed = sumMergedSeries(panelSeriesList, PANEL_FIELDS);
        const weatherSummed = sumMergedSeries(weatherSeriesList, WEATHER_FIELDS);
        const combined = combineByTime(panelSummed, weatherSummed).map(row => ({
          ...row,
          time: format(new Date(row._time), 'HH:mm:ss'),
          totalPower: row.power_w,
          totalVoltage: row.voltage_v,
          totalCurrent: row.current_a,
          avgTemp: row.temp_panel_c,
          avgLight: row.light_lvl
        }));
        setHistoryData(combined);
        setHistoryStatus(combined.length > 0 ? 'ok' : 'empty');
      })
      .catch(() => {
        if (cancelled) return;
        setHistoryData([]);
        setHistoryStatus('error');
      });

    return () => { cancelled = true; };
  }, [token, plantId, timeRange, panelIdsKey, stationIdsKey]);

  const loadForecast = useCallback(() => {
    if (!plantId) return;
    setForecastStatus('loading');
    fetchPlantForecast(token, plantId)
      .then((res) => { setForecast(res); setForecastStatus('ok'); })
      .catch(() => { setForecast(null); setForecastStatus('error'); });
  }, [token, plantId]);

  // Reset on plant change (a stale forecast from the previous plant must
  // never flash before the new one loads) and lazily fetch once either tab
  // that displays it is opened - forecasts change slowly, so there is no
  // need to poll it like real-time telemetry. 'overview' is included (and is
  // the default/landing tab) because the compact conditions summary now
  // lives there too.
  useEffect(() => { setForecast(null); setForecastStatus('idle'); }, [plantId]);
  useEffect(() => {
    if ((tab === 'overview' || tab === 'forecast') && forecastStatus === 'idle') loadForecast();
  }, [tab, forecastStatus, loadForecast]);

  // Active forecast risks for this plant - reuses the existing alerts API
  // (GET /api/alerts?status=active) rather than a second alert system; the
  // endpoint is scoped to plants the caller can already see (admin sees
  // all, a normal user only their assignments), and filtered to this
  // specific plantId + forecast-sourced rules client-side below.
  const loadPlantAlerts = useCallback(() => {
    if (!plantId) return;
    setPlantAlertsStatus('loading');
    fetchActiveAlerts(token, 100)
      .then((rows) => { setPlantAlerts(rows); setPlantAlertsStatus('ok'); })
      .catch(() => { setPlantAlerts([]); setPlantAlertsStatus('error'); });
  }, [token, plantId]);

  useEffect(() => { setPlantAlerts(null); setPlantAlertsStatus('idle'); }, [plantId]);
  useEffect(() => {
    if ((tab === 'overview' || tab === 'forecast') && plantAlertsStatus === 'idle') loadPlantAlerts();
  }, [tab, plantAlertsStatus, loadPlantAlerts]);

  // Real measured energy for THIS plant's own current local calendar day
  // (backend: EnergyService.getEnergyKwh over the plant's resolved timezone,
  // never a global/cross-plant figure and never mean(power_w) mislabeled as
  // energy - see historyApi.js/server.js for the measured/no_data/query_error
  // contract). Lazily fetched once when Aperçu is opened, like forecast/alerts.
  const loadEnergyToday = useCallback(() => {
    if (!plantId) return;
    setEnergyTodayStatus('loading');
    fetchPlantEnergyToday(token, plantId)
      .then((res) => { setEnergyToday(res); setEnergyTodayStatus('ok'); })
      .catch(() => { setEnergyToday(null); setEnergyTodayStatus('error'); });
  }, [token, plantId]);

  useEffect(() => { setEnergyToday(null); setEnergyTodayStatus('idle'); }, [plantId]);
  useEffect(() => {
    if (tab === 'overview' && energyTodayStatus === 'idle') loadEnergyToday();
  }, [tab, energyTodayStatus, loadEnergyToday]);

  // Live updates over the one shared Socket.IO connection (same event the
  // Alerts page already listens to) - merged by id, and dropped from this
  // ACTIVE-only list the moment a risk resolves, instead of waiting for the
  // next tab open/manual refresh.
  useEffect(() => {
    if (!socket) return;
    const onAlertUpdate = (incoming) => {
      if (incoming.plant_id !== plantId) return;
      setPlantAlerts((prev) => {
        const list = prev || [];
        const idx = list.findIndex((a) => a.id === incoming.id);
        if (incoming.status !== 'active') {
          return idx === -1 ? list : list.filter((a) => a.id !== incoming.id);
        }
        if (idx === -1) return [incoming, ...list];
        const next = [...list];
        next[idx] = incoming;
        return next;
      });
    };
    socket.on('alert_update', onAlertUpdate);
    return () => socket.off('alert_update', onAlertUpdate);
  }, [socket, plantId]);

  // Drives "Mis à jour il y a X min" / relative alert times on the Forecast
  // tab without needing to re-fetch anything - a slow tick is enough since
  // these labels only need to look approximately live, not to the second.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const forecastRisks = useMemo(
    () => (plantAlerts || []).filter((a) => a.plant_id === plantId && typeof a.ruleId === 'string' && a.ruleId.startsWith('forecast-')),
    [plantAlerts, plantId]
  );

  const nearTermIndex = useMemo(
    () => (forecast?.forecast_available ? findNearTermIndex(forecast.hourly, forecast.timezone) : -1),
    [forecast]
  );
  const nearTerm = nearTermIndex >= 0 ? forecast.hourly[nearTermIndex] : null;
  const hourlyUpcoming = forecast?.forecast_available
    ? forecast.hourly.slice(Math.max(nearTermIndex, 0), Math.max(nearTermIndex, 0) + 24)
    : [];

  const totalPower = plantPanels.reduce((sum, d) => sum + (d.power_w || 0), 0);
  const avgVoltage = plantPanels.length > 0 ? plantPanels.reduce((s, d) => s + (d.voltage_v || 0), 0) / plantPanels.length : 0;
  const avgCurrent = plantPanels.length > 0 ? plantPanels.reduce((s, d) => s + (d.current_a || 0), 0) / plantPanels.length : 0;
  const avgTemp = plantPanels.length > 0 ? plantPanels.reduce((s, d) => s + (d.temp_panel_c || 0), 0) / plantPanels.length : 0;
  const avgHumidity = plantWeather.length > 0 ? plantWeather.reduce((s, w) => s + (w.humidity_pct || 0), 0) / plantWeather.length : 0;
  const avgLight = plantWeather.length > 0 ? plantWeather.reduce((s, w) => s + (w.light_lvl || 0), 0) / plantWeather.length : 0;
  const avgWind = plantWeather.length > 0 ? plantWeather.reduce((s, w) => s + (w.wind_ms || 0), 0) / plantWeather.length : 0;

  // A photovoltaic "efficiency" KPI previously lived here
  // (power_w / (light_lvl * 10)) - removed (not renamed, not replaced): the
  // weather station's light_lvl is a unitless 0-100 normalized reading, not
  // measured irradiance in W/m2, and there is no panel area anywhere in this
  // system's telemetry. There is no physically valid way to compute panel
  // efficiency from what is actually measured.
  //
  // A RadialBarChart titled "Indicateurs de Performance" also previously
  // lived here, plotting avgLight (raw, already shown as-is on the Météo
  // tab - duplicated here, not derived) next to avgTemp/80*100 (an
  // arbitrary, undocumented rescale of a real °C reading onto a 0-100 gauge
  // - 80 is not STC, not a datasheet max operating temperature, just a
  // made-up divisor). Neither value is a PV performance indicator - not a
  // capacity ratio, not a specific yield, not a forecast comparison - so the
  // title overstated what was actually shown. Removed in favor of the one
  // KPI below that IS a genuine, real-data-backed monitoring indicator:
  // instantaneous capacity utilization (current measured power vs the
  // admin-configured installed capacity) - explicitly NOT "efficiency"/
  // "rendement" (nominalEfficiencyPct is a manufacturer datasheet spec, not
  // a live-measured quantity) and NOT "capacity factor" (which is defined
  // over an elapsed period, not an instant). Shows "Indisponible", never a
  // fabricated percentage, when installed capacity isn't configured.
  //
  // "No current telemetry" must never be presented as a measured 0% - a
  // plant that has literally never reported (e.g. Sidi ali Ben OUN: 900 kWp
  // configured, zero telemetry ever) is a fundamentally different situation
  // from a real panel genuinely producing 0 W right now, and totalPower
  // alone can't distinguish them (both sum to 0 over an empty or
  // all-zero array). online_panel_count is the existing signal that can:
  // it's computed server-side (server.js's offline-check interval) against
  // the real, admin-configurable "Panneau Hors Ligne" alert rule threshold -
  // the same staleness definition already shown as "Panneaux en ligne" in
  // the Installation summary directly above, and used identically on the
  // Devices/System Health pages. Reused here rather than inventing a new
  // frontend staleness rule, and deliberately NOT plantPanels.length (the
  // live Socket.IO cache, which starts empty every page load/refresh and
  // only reflects "received at least once this session" - it can't tell a
  // truly offline panel from one whose next update simply hasn't arrived
  // yet in this browser tab).
  const installedCapacityKw = plantMeta?.installed_capacity_kw ?? null;
  const hasCapacityConfigured = typeof installedCapacityKw === 'number' && installedCapacityKw > 0;
  const hasCurrentPanelTelemetry = (plantMeta?.online_panel_count ?? 0) > 0;
  const capacityUtilizationPct = (hasCapacityConfigured && hasCurrentPanelTelemetry)
    ? (totalPower / 1000 / installedCapacityKw) * 100
    : null;

  const renderChartState = () => {
    if (historyStatus === 'loading') return <LoadingState label="Chargement de l'historique..." />;
    if (historyStatus === 'error') return <ErrorState label="Erreur lors du chargement de l'historique" />;
    return <EmptyState label="Aucune donnée historique disponible pour cette période" />;
  };

  // Compact operational summary shown on Aperçu (the plant's landing tab) -
  // explicitly labeled as forecast/provider data (never "Conditions
  // Actuelles", which could read as the local ESP8266 sensor) and
  // deliberately omits lat/lon/elevation/timezone, which stay available in
  // Plant Management for admins but are too technical for the main
  // monitoring view. Reuses the exact same forecast state as the Prévisions
  // tab - no second fetch, no duplicated full section.
  const renderForecastConditionsSummary = () => (
    <div className="section">
      <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Conditions Météo Prévues Actuellement</h4>
      {forecastStatus === 'loading' && <LoadingState label="Chargement des conditions météo..." />}
      {forecastStatus === 'error' && <ErrorState label="Prévisions météo temporairement indisponibles" />}
      {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'no_coordinates' && (
        <EmptyState label="Prévisions non disponibles — coordonnées de la centrale non configurées" />
      )}
      {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'provider_error' && (
        <ErrorState label="Prévisions météo temporairement indisponibles" />
      )}
      {forecastStatus === 'ok' && forecast?.forecast_available && nearTerm && (
        <>
          <div className="summary-grid summary-grid-compact">
            <div className="summary-card">
              <div className="summary-label">Condition</div>
              <div className="summary-value" style={{ fontSize: '1.15rem' }} title={wmoLabel(nearTerm.weather_code)}>{wmoIcon(nearTerm.weather_code)} {wmoLabel(nearTerm.weather_code)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Température</div>
              <div className="summary-value">{nearTerm.temperature_2m?.toFixed(1)}<span className="summary-unit">°C</span></div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Humidité</div>
              <div className="summary-value">
                {nearTerm.relative_humidity_2m != null ? nearTerm.relative_humidity_2m : '—'}
                {nearTerm.relative_humidity_2m != null && <span className="summary-unit">%</span>}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Pluie</div>
              <div className="summary-value">{nearTerm.precipitation_probability ?? 0}<span className="summary-unit">%</span></div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Vent</div>
              <div className="summary-value">{nearTerm.wind_speed_10m?.toFixed(1)}<span className="summary-unit"> m/s</span></div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Rafales</div>
              <div className="summary-value">{nearTerm.wind_gusts_10m?.toFixed(1)}<span className="summary-unit"> m/s</span></div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Nuages</div>
              <div className="summary-value">{nearTerm.cloud_cover ?? 0}<span className="summary-unit">%</span></div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Rayonnement</div>
              <div className="summary-value">{nearTerm.shortwave_radiation ?? 0}<span className="summary-unit"> W/m²</span></div>
            </div>
          </div>
          <div className="summary-sub" style={{ marginTop: 4 }}>Prévisions mises à jour {formatRelative(forecast.generatedAt, now)}</div>
        </>
      )}
    </div>
  );

  // Active forecast-risk cards, plant-scoped, ACTIVE-only - same data/socket
  // wiring as the Prévisions tab used to render inline (now only rendered
  // here, on Aperçu, per this task - not duplicated in both places).
  const renderForecastRisks = () => (
    <div className="section">
      <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Risques Météo Prévus</h4>
      {plantAlertsStatus === 'loading' && <LoadingState label="Chargement des risques..." />}
      {plantAlertsStatus === 'error' && <ErrorState label="Impossible de charger les risques météo" />}
      {plantAlertsStatus === 'ok' && forecastRisks.length === 0 && (
        <EmptyState label="Aucun risque météo actif prévu" />
      )}
      {plantAlertsStatus === 'ok' && forecastRisks.length > 0 && (
        <ul className="alert-card-list">
          {forecastRisks.map((a) => (
            <li key={a.id} className={`alert-card alert-card-${(a.severity || 'info').toLowerCase()}`}>
              <div className="alert-card-top">
                <span className={`severity-badge severity-badge-${(a.severity || 'info').toLowerCase()}`}>{SEVERITY_LABELS[a.severity] || a.severity}</span>
              </div>
              <div className="alert-card-title">{a.title}</div>
              <div className="alert-card-message">{trimForecastMessage(a.message)}</div>
              <div className="alert-card-times"><span>Depuis {formatRelative(a.triggeredAt, now)}</span></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // Compact installation summary (admin-configured installed capacity/panel
  // count/model vs telemetry-derived detected/online counts - never mixed
  // together into one number). Renders nothing at all when the admin hasn't
  // configured anything yet, rather than an empty section - existing plants
  // with no installation metadata (e.g. gabes today) must see no change here.
  const renderInstallationSummary = () => {
    if (!plantMeta) return null;
    const hasAnyInstallData = plantMeta.installed_capacity_kw !== null || plantMeta.installed_panel_count !== null
      || plantMeta.panel_manufacturer || plantMeta.panel_model || plantMeta.commissioning_date;
    if (!hasAnyInstallData) return null;

    const installedCount = plantMeta.installed_panel_count;
    const detectedCount = plantMeta.detected_panel_count ?? plantMeta.panel_count ?? 0;
    const missingCount = (typeof installedCount === 'number' && installedCount > detectedCount) ? installedCount - detectedCount : 0;
    const modelLabel = [plantMeta.panel_manufacturer, plantMeta.panel_model].filter(Boolean).join(' ');

    return (
      <div className="section">
        <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Installation</h4>
        <div className="summary-grid summary-grid-compact">
          {plantMeta.installed_capacity_kw !== null && (
            <div className="summary-card">
              <div className="summary-label">Puissance installée</div>
              <div className="summary-value">{plantMeta.installed_capacity_kw.toFixed(2)}<span className="summary-unit"> kWp</span></div>
            </div>
          )}
          {installedCount !== null && (
            <div className="summary-card">
              <div className="summary-label">Panneaux installés</div>
              <div className="summary-value">{installedCount}</div>
            </div>
          )}
          <div className="summary-card">
            <div className="summary-label">Panneaux détectés</div>
            <div className="summary-value">{detectedCount}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Panneaux en ligne</div>
            <div className="summary-value">{plantMeta.online_panel_count ?? 0}</div>
          </div>
          {modelLabel && (
            <div className="summary-card">
              <div className="summary-label">Modèle</div>
              <div className="summary-value" style={{ fontSize: '1rem' }}>{modelLabel}</div>
            </div>
          )}
          {plantMeta.panel_rated_power_wp !== null && (
            <div className="summary-card">
              <div className="summary-label">Puissance nominale</div>
              <div className="summary-value">{plantMeta.panel_rated_power_wp}<span className="summary-unit"> Wp</span></div>
            </div>
          )}
          {plantMeta.commissioning_date && (
            <div className="summary-card">
              <div className="summary-label">Mise en service</div>
              <div className="summary-value" style={{ fontSize: '1rem' }}>{plantMeta.commissioning_date}</div>
            </div>
          )}
        </div>
        {missingCount > 0 && (
          <div className="summary-sub" style={{ marginTop: 8 }}>
            {missingCount} panneau{missingCount > 1 ? 'x' : ''} installé{missingCount > 1 ? 's' : ''} non détecté{missingCount > 1 ? 's' : ''}.
            Cela peut être normal (appareil pas encore provisionné, télémétrie non reçue, maintenance) ou indiquer un problème matériel à vérifier.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Centrales', to: '/plants' }, { label: plantMeta?.name || plantId }]} />

      <div className="page-header">
        <h1><Sun size={22} className="icon-sun" /> {plantMeta?.name || plantId}</h1>
      </div>

      {plantMeta && (plantMeta.location || plantMeta.description || plantMeta.active === false) && (
        <div className="summary-card" style={{ marginBottom: 20 }}>
          {plantMeta.location && <div className="plant-card-location"><MapPin size={13} /> {plantMeta.location}</div>}
          {plantMeta.description && <div className="summary-sub" style={{ marginTop: 6 }}>{plantMeta.description}</div>}
          {plantMeta.active === false && <div className="plant-card-inactive-tag">Inactif</div>}
          {!plantMeta.has_telemetry && (
            <div className="plant-card-offline-tag" style={{ marginTop: 8 }}><WifiOff size={13} /> Aucune donnée télémétrique reçue pour cette centrale</div>
          )}
        </div>
      )}

      <div className="tabs">
        {TABS.map(t => (
          <button key={t.id} className={`tab-button${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          {/* Operational status first: weather summary + active forecast
              risks are what an operator needs to notice immediately, ahead
              of the slower-moving production KPIs/chart below. */}
          {renderForecastConditionsSummary()}
          {renderForecastRisks()}
          {renderInstallationSummary()}

          <div className="kpi-grid">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon"><Zap /></div>
              <div className="kpi-content"><div className="kpi-label">Puissance Totale</div><div className="kpi-value">{totalPower.toFixed(2)}</div><div className="kpi-unit">W</div></div>
            </div>
            <div className="kpi-card kpi-success">
              <div className="kpi-icon"><Battery /></div>
              <div className="kpi-content"><div className="kpi-label">Tension Moyenne</div><div className="kpi-value">{avgVoltage.toFixed(2)}</div><div className="kpi-unit">V</div></div>
            </div>
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon"><Thermometer /></div>
              <div className="kpi-content"><div className="kpi-label">Température Moyenne</div><div className="kpi-value">{avgTemp.toFixed(1)}</div><div className="kpi-unit">°C</div></div>
            </div>
            <div className="kpi-card kpi-secondary">
              <div className="kpi-icon"><Activity /></div>
              <div className="kpi-content"><div className="kpi-label">Courant Moyen</div><div className="kpi-value">{avgCurrent.toFixed(2)}</div><div className="kpi-unit">A</div></div>
            </div>
            <div className="kpi-card kpi-accent">
              <div className="kpi-icon"><Clock /></div>
              {energyTodayStatus === 'ok' && energyToday?.data_status === 'measured' ? (
                <div className="kpi-content"><div className="kpi-label">Énergie Aujourd'hui</div><div className="kpi-value">{energyToday.energy_kwh.toFixed(2)}</div><div className="kpi-unit">kWh</div></div>
              ) : energyTodayStatus === 'ok' && energyToday?.data_status === 'no_data' ? (
                <div className="kpi-content"><div className="kpi-label">Énergie Aujourd'hui</div><div className="kpi-value" style={{ fontSize: '1rem' }}>Aucune donnée</div></div>
              ) : energyTodayStatus === 'error' || (energyTodayStatus === 'ok' && energyToday?.data_status === 'query_error') ? (
                <div className="kpi-content"><div className="kpi-label">Énergie Aujourd'hui</div><div className="kpi-value" style={{ fontSize: '1rem' }}>Indisponible</div></div>
              ) : (
                <div className="kpi-content"><div className="kpi-label">Énergie Aujourd'hui</div><div className="kpi-value" style={{ fontSize: '1rem' }}>…</div></div>
              )}
            </div>
            <div className="kpi-card kpi-info">
              <div className="kpi-icon"><Gauge /></div>
              {!hasCapacityConfigured ? (
                <div className="kpi-content"><div className="kpi-label">Utilisation Instantanée</div><div className="kpi-value" style={{ fontSize: '1rem' }}>Indisponible</div></div>
              ) : !hasCurrentPanelTelemetry ? (
                <div className="kpi-content"><div className="kpi-label">Utilisation Instantanée</div><div className="kpi-value" style={{ fontSize: '1rem' }}>Aucune donnée</div></div>
              ) : (
                <div className="kpi-content"><div className="kpi-label">Utilisation Instantanée</div><div className="kpi-value">{capacityUtilizationPct.toFixed(1)}</div><div className="kpi-unit">%</div></div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'panels' && (
        <div className="panels-section">
          {plantPanels.length === 0 ? (
            <EmptyState label="En attente des données ESP32..." />
          ) : (
            <div className="panels-grid-advanced">
              {plantPanels.map((data, index) => {
                // Arbitrary, undocumented thresholds (no datasheet/alert-rule
                // basis - not tied to installed capacity, configured alert
                // thresholds, or any manufacturer spec). Deliberately drive
                // ONLY the card's color/glow (status-excellent/good/low,
                // temp-hot/warm/normal in AdvancedDashboard.css) - the words
                // themselves are never rendered as visible text anywhere, so
                // this is a coarse "at a glance" visual cue, not a claimed
                // measurement, status, or alert. Left as-is (not redesigned):
                // it's a real, if unexplained, existing convention on this
                // page and outside this task's scope to overhaul.
                const powerStatus = data.power_w > 50 ? 'excellent' : data.power_w > 20 ? 'good' : 'low';
                const tempStatus = data.temp_panel_c > 70 ? 'hot' : data.temp_panel_c > 50 ? 'warm' : 'normal';
                return (
                  <div key={index} className={`panel-card-advanced status-${powerStatus}`} onClick={() => navigate(`/plants/${plantId}/panels/${data.panel_id}`)} style={{ cursor: 'pointer' }}>
                    <div className="panel-header">
                      <div className="panel-id"><Sun size={20} /><span>Panneau {data.panel_id}</span></div>
                      <div className={`status-indicator ${powerStatus}`}></div>
                    </div>
                    <div className="panel-main-metric"><div className="metric-value">{(data.power_w || 0).toFixed(1)}</div><div className="metric-unit">W</div></div>
                    <div className="panel-metrics-grid">
                      <div className="mini-metric"><Battery size={14} /><span>{(data.voltage_v || 0).toFixed(1)} V</span></div>
                      <div className="mini-metric"><Activity size={14} /><span>{(data.current_a || 0).toFixed(2)} A</span></div>
                      <div className={`mini-metric temp-${tempStatus}`}><Thermometer size={14} /><span>{(data.temp_panel_c || 0).toFixed(1)}°C</span></div>
                    </div>
                    <div className="panel-footer">
                      <span className="update-time"><Clock size={12} />{data.lastUpdate ? format(new Date(data.lastUpdate), 'HH:mm:ss') : format(new Date(), 'HH:mm:ss')}</span>
                      <ChevronRight size={16} className="chevron-icon" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'weather' && (
        <div className="section">
          {plantWeather.length === 0 ? (
            <EmptyState label="Aucune donnée météo pour cette centrale" />
          ) : (
            <div className="kpi-grid">
              <div className="kpi-card kpi-info">
                <div className="kpi-icon"><Thermometer /></div>
                <div className="kpi-content"><div className="kpi-label">Air</div><div className="kpi-value">{(plantWeather[0].temp_air_c || 0).toFixed(1)}</div><div className="kpi-unit">°C</div></div>
              </div>
              <div className="kpi-card kpi-secondary">
                <div className="kpi-icon"><CloudRain /></div>
                <div className="kpi-content"><div className="kpi-label">Humidité</div><div className="kpi-value">{avgHumidity.toFixed(0)}</div><div className="kpi-unit">%</div></div>
              </div>
              <div className="kpi-card kpi-success">
                <div className="kpi-icon"><Sun /></div>
                <div className="kpi-content"><div className="kpi-label">Lumière</div><div className="kpi-value">{avgLight.toFixed(0)}</div><div className="kpi-unit">%</div></div>
              </div>
              <div className="kpi-card kpi-secondary">
                <div className="kpi-icon"><Activity /></div>
                <div className="kpi-content"><div className="kpi-label">Vent</div><div className="kpi-value">{avgWind.toFixed(1)}</div><div className="kpi-unit">m/s</div></div>
              </div>
              <div className="kpi-card">
                <div className="kpi-content"><div className="kpi-label">Station</div><div className="kpi-value" style={{ fontSize: '1.2rem' }}>{plantWeather[0].station_id || 'WS1'}</div></div>
              </div>
              <div className="kpi-card">
                <div className="kpi-content"><div className="kpi-label">Dernière mise à jour</div><div className="kpi-value" style={{ fontSize: '1.2rem' }}>{plantWeather[0].lastUpdate ? format(new Date(plantWeather[0].lastUpdate), 'HH:mm:ss') : format(new Date(), 'HH:mm:ss')}</div></div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="section">
          <div className="section-header-row">
            <h3 className="section-title">Historique</h3>
            <div className="time-range-selector">
              <button className={timeRange === '1h' ? 'active' : ''} onClick={() => setTimeRange('1h')}>1H</button>
              <button className={timeRange === '6h' ? 'active' : ''} onClick={() => setTimeRange('6h')}>6H</button>
              <button className={timeRange === '24h' ? 'active' : ''} onClick={() => setTimeRange('24h')}>24H</button>
              <button className={timeRange === '7d' ? 'active' : ''} onClick={() => setTimeRange('7d')}>7J</button>
            </div>
          </div>

          <div className="chart-panel chart-main">
            <div className="chart-header">
              <h3>Production</h3>
              <div className="metric-selector">
                <button className={selectedMetric === 'power' ? 'active' : ''} onClick={() => setSelectedMetric('power')}>Puissance</button>
                <button className={selectedMetric === 'voltage' ? 'active' : ''} onClick={() => setSelectedMetric('voltage')}>Tension</button>
                <button className={selectedMetric === 'current' ? 'active' : ''} onClick={() => setSelectedMetric('current')}>Courant</button>
              </div>
            </div>
            {historyStatus !== 'ok' ? renderChartState() : (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={historyData}>
                  <defs>
                    <linearGradient id="colorPower" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/><stop offset="95%" stopColor="#8884d8" stopOpacity={0}/></linearGradient>
                    <linearGradient id="colorVoltage" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/><stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/></linearGradient>
                    <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ffc658" stopOpacity={0.8}/><stop offset="95%" stopColor="#ffc658" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="time" stroke="#888" tick={{ fill: '#888' }} />
                  <YAxis stroke="#888" tick={{ fill: '#888' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                  <Legend />
                  {selectedMetric === 'power' && <Area type="monotone" dataKey="totalPower" stroke="#8884d8" fillOpacity={1} fill="url(#colorPower)" name="Puissance (W)" connectNulls={false} />}
                  {selectedMetric === 'voltage' && <Area type="monotone" dataKey="totalVoltage" stroke="#82ca9d" fillOpacity={1} fill="url(#colorVoltage)" name="Tension (V)" connectNulls={false} />}
                  {selectedMetric === 'current' && <Area type="monotone" dataKey="totalCurrent" stroke="#ffc658" fillOpacity={1} fill="url(#colorCurrent)" name="Courant (A)" connectNulls={false} />}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="chart-panel" style={{ marginTop: 16 }}>
            <div className="chart-header"><h3>Analyse Multi-Paramètres</h3></div>
            {historyStatus !== 'ok' ? renderChartState() : (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={historyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="time" stroke="#888" tick={{ fill: '#888' }} />
                  <YAxis yAxisId="left" stroke="#888" tick={{ fill: '#888' }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#888" tick={{ fill: '#888' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="totalPower" fill="#8884d8" name="Puissance (W)" />
                  <Line yAxisId="right" type="monotone" dataKey="avgTemp" stroke="#ff8042" name="Température (°C)" connectNulls={false} />
                  <Line yAxisId="right" type="monotone" dataKey="avgLight" stroke="#ffc658" name="Luminosité (%)" connectNulls={false} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {tab === 'forecast' && (
        <div className="section">
          {forecastStatus === 'loading' && <LoadingState label="Chargement des prévisions..." />}
          {forecastStatus === 'error' && <ErrorState label="Impossible de charger les prévisions météo" />}

          {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'no_coordinates' && (
            <EmptyState label={
              user?.role === 'admin'
                ? 'Coordonnées de la centrale non configurées. Ajoutez-les dans Gestion des Centrales pour activer les prévisions météo.'
                : 'Coordonnées de la centrale non configurées.'
            } />
          )}
          {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'provider_error' && (
            <ErrorState label="Prévisions météo temporairement indisponibles" />
          )}

          {forecastStatus === 'ok' && forecast?.forecast_available && (
            <>
              <div className="section-header-row">
                <div>
                  <h3 className="section-title" style={{ marginBottom: 4 }}><CloudSun size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Prévisions Météo</h3>
                  <div className="summary-sub">
                    {forecast.coordinates.latitude.toFixed(4)}, {forecast.coordinates.longitude.toFixed(4)}
                    {forecast.elevation !== null && ` · ${forecast.elevation} m`}
                    {forecast.timezone && ` · ${forecast.timezone}`}
                    {' · Mis à jour '}{formatRelative(forecast.generatedAt, now)}
                  </div>
                </div>
                <button className="btn-secondary" onClick={() => { loadForecast(); loadPlantAlerts(); }}><RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Actualiser</button>
              </div>

              {/* Compact current-conditions + active-risks summary now lives
                  on the Aperçu tab instead (see renderForecastConditionsSummary/
                  renderForecastRisks above) - not duplicated here. */}

              {/* Prévisions journalières */}
              <div className="section" style={{ marginTop: 18 }}>
                <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Prévisions Journalières</h4>
                <div className="panels-grid-advanced">
                  {forecast.daily.map((day) => (
                    <div key={day.date} className="panel-card-advanced">
                      <div className="panel-header">
                        <div className="panel-id"><span>{localDateLabel(day.date)}</span></div>
                      </div>
                      <div className="panel-main-metric" title={wmoLabel(day.weather_code)}>
                        <div className="metric-value" style={{ fontSize: '1.6rem' }}>{wmoIcon(day.weather_code)}</div>
                      </div>
                      <div className="summary-sub" style={{ textAlign: 'center', marginBottom: 8 }}>{wmoLabel(day.weather_code)}</div>
                      <div className="panel-metrics-grid">
                        <div className="mini-metric"><Thermometer size={14} /><span>{day.temperature_2m_min?.toFixed(0)}° / {day.temperature_2m_max?.toFixed(0)}°C</span></div>
                        <div className="mini-metric"><Droplets size={14} /><span>{day.precipitation_sum?.toFixed(1)} mm ({day.precipitation_probability_max ?? 0}%)</span></div>
                        <div className="mini-metric"><Wind size={14} /><span>{day.wind_gusts_10m_max?.toFixed(1)} m/s</span></div>
                      </div>
                      <div className="device-card-row" style={{ marginTop: 8 }}>
                        Ensoleillement : {((day.sunshine_duration || 0) / 3600).toFixed(1)} h
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prévisions horaires (24h) */}
              <div className="section">
                <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Prévisions Horaires (24h)</h4>
                <div className="hourly-forecast-grid">
                  {hourlyUpcoming.map((h) => (
                    <div key={h.time} className="hourly-forecast-card">
                      <div className="hourly-forecast-time">{localHour(h.time)}</div>
                      <div className="hourly-forecast-icon" title={wmoLabel(h.weather_code)}>{wmoIcon(h.weather_code)}</div>
                      <div className="hourly-forecast-temp">{h.temperature_2m?.toFixed(0)}°C</div>
                      <div className="hourly-forecast-row"><Droplets size={11} />{h.precipitation_probability ?? 0}%</div>
                      <div className="hourly-forecast-row"><Wind size={11} />{h.wind_speed_10m?.toFixed(0)}/{h.wind_gusts_10m?.toFixed(0)} m/s</div>
                      <div className="hourly-forecast-row">☁️ {h.cloud_cover ?? 0}%</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mesure locale vs prévision */}
              <div className="section">
                <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Mesure Locale vs Prévision</h4>
                {plantWeather[0] ? (
                  <>
                    <div className="summary-sub" style={{ marginBottom: 8 }}>
                      Dernière mesure locale : {plantWeather[0].lastUpdate ? formatRelative(plantWeather[0].lastUpdate, now) : 'inconnue'}
                      {plantWeather[0].station_id && ` (station ${plantWeather[0].station_id})`}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead><tr><th>Mesure</th><th>Mesure locale</th><th>Prévision Open-Meteo</th></tr></thead>
                        <tbody>
                          <tr>
                            <td>Température</td>
                            <td>{typeof plantWeather[0].temp_air_c === 'number' ? `${plantWeather[0].temp_air_c.toFixed(1)}°C` : '—'}</td>
                            <td>{nearTerm?.temperature_2m != null ? `${nearTerm.temperature_2m.toFixed(1)}°C` : '—'}</td>
                          </tr>
                          <tr>
                            <td>Humidité</td>
                            <td>{typeof plantWeather[0].humidity_pct === 'number' ? `${plantWeather[0].humidity_pct.toFixed(0)}%` : '—'}</td>
                            <td>{nearTerm?.relative_humidity_2m != null ? `${nearTerm.relative_humidity_2m}%` : '—'}</td>
                          </tr>
                          <tr>
                            <td>Vent</td>
                            <td>{typeof plantWeather[0].wind_ms === 'number' ? `${plantWeather[0].wind_ms.toFixed(1)} m/s` : '—'}</td>
                            <td>{nearTerm?.wind_speed_10m != null ? `${nearTerm.wind_speed_10m.toFixed(1)} m/s` : '—'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : <EmptyState label="Aucune donnée de station locale pour cette centrale" />}
              </div>

              {/* Attribution */}
              <div className="summary-sub" style={{ textAlign: 'center', marginTop: 8 }}>Données météo fournies par Open-Meteo.com</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
